import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import type { LabelTemplate } from '@etiquetachef/types';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { LabelTemplatesTable } from '@/features/labelTemplates/LabelTemplatesTable';
import { toast } from '@/lib/notifications';
import { useLabelTemplates } from '@/features/labelTemplates/useLabelTemplates';
import {
  deleteLabelTemplateCallable,
  resetDefaultLabelTemplatesCallable,
  seedDefaultLabelTemplatesForExistingTenantCallable,
  setDefaultLabelTemplateCallable,
} from '@/lib/callables';

type Props = {
  tenantId: string;
};

/**
 * Aba Modelos do tenant: lista os modelos de etiqueta e permite definir o
 * padrão, excluir, semear os modelos padrão (tenant sem nenhum) e resetá-los.
 *
 * Origem: sem issue — commit a5ee68045, 2026-04-29 21:29 +0200 (Sprint 15 PR
 * D, Label Templates por tenant).
 *
 * Contrato: `setDefaultLabelTemplate`/`deleteLabelTemplate` (`{ tenantId,
 * templateId }`, chamados pela tabela); `seedDefaultLabelTemplatesForExistingTenant`
 * devolve `{ seeded, count }` (no-op com aviso se já há modelos);
 * `resetDefaultLabelTemplates` devolve `{ deletedCount, recreatedCount }` e
 * pede confirmação — apaga os modelos de fábrica (reconhecidos pelo nome) e
 * os recria, preservando os criados pelo cliente; modelo do cliente renomeado
 * com nome de fábrica vai junto (limitação do callable). Erro do semear vira
 * toast com a mensagem do servidor; erro do reset fica num `Alert` dentro do
 * confirm, que continua aberto (#1021).
 *
 * Usado por: `routes/TenantDetailPage.tsx` (aba Modelos).
 *
 * Dados: `tenants/{tenantId}/labelTemplates` (`useLabelTemplates`).
 *
 * Histórico:
 *  - 2026-10-25 15:00 +0100 — (#1021) erro do reset em `Alert` no confirm: o
 *    toast ficava atrás do overlay (mesmo achado do CRM, #174).
 */
export function LabelTemplatesTab({ tenantId }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const result = useLabelTemplates(tenantId);
  const [seeding, setSeeding] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  // #1021 (2026-10-25 15:00 +0100): erro do reset, mostrado dentro do confirm.
  const [resetError, setResetError] = useState<string | null>(null);

  async function handleSetDefault(template: LabelTemplate): Promise<void> {
    await setDefaultLabelTemplateCallable({ tenantId, templateId: template.id });
  }
  async function handleDelete(template: LabelTemplate): Promise<void> {
    await deleteLabelTemplateCallable({ tenantId, templateId: template.id });
  }

  async function handleSeed(): Promise<void> {
    setSeeding(true);
    try {
      const out = await seedDefaultLabelTemplatesForExistingTenantCallable({ tenantId });
      if (out.seeded) {
        toast.success(t('labelTemplates.toast.seeded', { count: out.count }));
      } else {
        toast.message(t('labelTemplates.toast.alreadyHasTemplates'));
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSeeding(false);
    }
  }

  async function handleReset(): Promise<void> {
    setResetting(true);
    setResetError(null);
    try {
      const out = await resetDefaultLabelTemplatesCallable({ tenantId });
      toast.success(
        t('labelTemplates.toast.reset', {
          deleted: out.deletedCount,
          recreated: out.recreatedCount,
        }),
      );
      setResetOpen(false);
    } catch (err) {
      // Auditoria 2026-10-25 15:00 +0100 (#1021): era `toast.error`, atrás do overlay do confirm, que
      // não fecha no erro (mesmo achado do CRM, #174). Vai para o `Alert` dentro dele.
      setResetError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setResetting(false);
    }
  }

  if (result.kind === 'LOADING') {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />
      </Box>
    );
  }
  if (result.kind === 'ERROR') {
    return <Alert type="error">{t('labelTemplates.errorLoad')}</Alert>;
  }

  const templates = result.templates;

  return (
    <SpaceBetween size="m">
      <Header
        variant="h2"
        description={t('labelTemplates.description')}
        actions={
          templates.length > 0 ? (
            <Button
              variant="link"
              onClick={(): void => {
                setResetError(null);
                setResetOpen(true);
              }}
              disabled={resetting}
            >
              {t('labelTemplates.actions.resetDefaults')}
            </Button>
          ) : undefined
        }
      >
        {t('labelTemplates.title')}
      </Header>

      {templates.length === 0 ? (
        <Container>
          <Box textAlign="center" padding="m">
            <SpaceBetween size="s">
              <Box color="text-body-secondary">{t('labelTemplates.empty.message')}</Box>
              <Button
                variant="primary"
                onClick={(): void => {
                  void handleSeed();
                }}
                loading={seeding}
              >
                {t('labelTemplates.empty.seedButton')}
              </Button>
            </SpaceBetween>
          </Box>
        </Container>
      ) : (
        <LabelTemplatesTable
          templates={templates}
          onSetDefault={handleSetDefault}
          onDelete={handleDelete}
        />
      )}

      <AdminConfirm
        visible={resetOpen}
        title={t('labelTemplates.resetDialog.title')}
        confirmLabel={t('labelTemplates.resetDialog.confirm')}
        cancelLabel={t('tenantDetail.actions.cancel')}
        loading={resetting}
        onConfirm={(): void => {
          void handleReset();
        }}
        onDismiss={(): void => {
          setResetOpen(false);
        }}
      >
        <SpaceBetween size="m">
          <Box variant="p">{t('labelTemplates.resetDialog.description')}</Box>
          {/* Auditoria 2026-10-25 15:00 +0100 (#1021): erro dentro do confirm, junto do footer (#174). */}
          {resetError !== null && <Alert type="error">{resetError}</Alert>}
        </SpaceBetween>
      </AdminConfirm>
    </SpaceBetween>
  );
}
